import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { API_BASE } from './apiConfig';
import { useLinkedPlayers, invalidateLinked } from './api/useLinkedPlayers';
import { useOwnLiveSession } from './SocketContext';
import { useTheme } from './ThemeContext';
import { Button, DataTable, DotLabel, Icon, Segmented, Skel, StatusBadge, useToast } from './components/ui';
import RangeOfMotionChart from './RangeOfMotionChart';
import GameReviewsChart from './GameReviewsChart';
import './PlayerProfilePage.css';
import { gameLabel } from './games';
import { GameCell, startedByLabel } from './sessionAccess';
import { experienceLabel, isExperience, serverErrorText } from './accountFacts';

const toMs = (x) => {
    if (x == null || x === '') return null;
    if (typeof x === 'number') return x;
    const n = Number(x);
    if (Number.isFinite(n)) return n;
    const p = Date.parse(x);
    return Number.isFinite(p) ? p : null;
};
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
function fmtWhen(ms) {
    if (!ms) return '—';
    const d = new Date(ms);
    const now = new Date();
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (sameDay(d, now)) return `Today, ${time}`;
    const y = new Date(now);
    y.setDate(now.getDate() - 1);
    if (sameDay(d, y)) return `Yesterday, ${time}`;
    return d.toLocaleDateString([], d.getFullYear() === now.getFullYear() ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtLength(s) {
    const a = toMs(s.startTime);
    const b = toMs(s.endTime);
    if (!a || !b || b <= a) return '—';
    const sec = Math.round((b - a) / 1000);
    const m = Math.floor(sec / 60);
    return `${m}:${String(sec - m * 60).padStart(2, '0')}`;
}
const greetingFor = (h) => (h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening');

/** results.jointsData may be an array [{_name, angleMin, angleMax}] or an object keyed by joint name. */
function jointList(jd) {
    if (Array.isArray(jd)) return jd;
    if (jd && typeof jd === 'object') return Object.entries(jd).map(([name, v]) => (v && typeof v === 'object' ? { name, ...v } : null)).filter(Boolean);
    return [];
}

/** "You've played 9 times this month — 3 more than last month." from real session start times. */
function activityLine(sessions) {
    if (!sessions.length) return 'No games yet';
    const now = new Date();
    const thisM = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const lastM = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
    let a = 0;
    let b = 0;
    let latest = 0;
    sessions.forEach((s) => {
        const t = toMs(s.startTime);
        if (!t) return;
        if (t >= thisM) a += 1;
        else if (t >= lastM) b += 1;
        if (t > latest) latest = t;
    });
    if (!a) {
        return latest
            ? `No games this month · last played ${new Date(latest).toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' })}`
            : 'No games this month';
    }
    const times = a === 1 ? 'once' : `${a} times`;
    if (!b) return `You’ve played ${times} this month.`;
    const d = a - b;
    const cmp = d > 0 ? `${d} more than last month` : d < 0 ? `${-d} fewer than last month` : 'the same as last month';
    return `You’ve played ${times} this month — ${cmp}.`;
}

/** GET /api/sessions?user_id= — 200 array | 204 empty body (never res.json() a 204). */
async function fetchSessions(authFetch, url) {
    const res = await authFetch(url);
    if (res.status === 204) return [];
    if (!res.ok) throw new Error(`Sessions didn’t load (${res.status})`);
    const text = await res.text();
    if (!text.trim()) return [];
    const data = JSON.parse(text);
    return Array.isArray(data) ? data : [];
}

function SectionError({ what, onRetry }) {
    return (
        <div role="alert" className="pld-alert">
            <span className="pld-alert__text"><strong>{what} didn’t load.</strong></span>
            <Button variant="secondary" size="lg" onClick={onRetry}>Try again</Button>
        </div>
    );
}

function PlayerProfilePage() {
    const { user, authFetch } = useAuth();
    const navigate = useNavigate();
    const toast = useToast();
    const { theme, setTheme } = useTheme();
    const { providers, loading: linkLoading, error: linkError, reload: reloadLinks, nameOf } = useLinkedPlayers();
    const own = useOwnLiveSession();

    // Own sessions → range of motion, activity line, recent sessions.
    const [sess, setSess] = useState({ list: [], loading: true, error: null });
    const loadSessions = useCallback(async () => {
        if (!user?.user_id) return;
        setSess((s) => ({ ...s, loading: true, error: null }));
        try {
            const list = await fetchSessions(authFetch, `${API_BASE}/api/sessions?user_id=${encodeURIComponent(user.user_id)}`);
            setSess({ list, loading: false, error: null });
        } catch (e) {
            setSess({ list: [], loading: false, error: e.message || 'Sessions didn’t load' });
        }
    }, [authFetch, user]);
    useEffect(() => { loadSessions(); }, [loadSessions]);

    // Game reviews (post-game ratings).
    const [rev, setRev] = useState({ list: [], loading: true, error: null });
    const loadReviews = useCallback(async () => {
        if (!user?.user_id) return;
        setRev((s) => ({ ...s, loading: true, error: null }));
        try {
            const res = await authFetch(`${API_BASE}/api/ratings/historical?user_id=${encodeURIComponent(user.user_id)}`);
            if (res.status === 204 || res.status === 404) {
                setRev({ list: [], loading: false, error: null });
                return;
            }
            if (!res.ok) throw new Error(`Reviews didn’t load (${res.status})`);
            const text = await res.text();
            const data = text.trim() ? JSON.parse(text) : [];
            const list = (Array.isArray(data) ? data : []).map((r) => ({
                name: r.Name,
                lastPlayDate: r.LastPlayDate,
                timeStamp: r.timestamp,
                happiness: r.Happiness,
                excitement: r.Excitement,
                control: r.Control,
                pain: r.Pain,
            }));
            setRev({ list, loading: false, error: null });
        } catch (e) {
            setRev({ list: [], loading: false, error: e.message || 'Reviews didn’t load' });
        }
    }, [authFetch, user]);
    useEffect(() => { loadReviews(); }, [loadReviews]);

    const jointsData = useMemo(() => {
        const out = [];
        sess.list.forEach((s) => {
            jointList(s?.results?.jointsData).forEach((j) => {
                out.push({
                    game: s.game ? gameLabel(s.game) : s.game,
                    timestamp: s.startTime,
                    startTime: s.startTime,
                    endTime: s.endTime,
                    name: j._name || j.name,
                    angleMin: j.angleMin,
                    angleMax: j.angleMax,
                });
            });
        });
        return out;
    }, [sess.list]);

    const recent = useMemo(
        () => [...sess.list].filter((s) => toMs(s.startTime)).sort((a, b) => toMs(b.startTime) - toMs(a.startTime)).slice(0, 5),
        [sess.list],
    );

    const [confirm, setConfirm] = useState(null);
    const [busy, setBusy] = useState(null);
    const unlink = async (p) => {
        setBusy(p.link_id);
        try {
            const res = await authFetch(`${API_BASE}/api/users/${user.user_id}/linked/${p.link_id}`, { method: 'DELETE' });
            if (!res.ok) {
                let msg = '';
                try { msg = serverErrorText(await res.json()); } catch { /* empty body */ }
                throw new Error(msg || `The server returned ${res.status}.`);
            }
            invalidateLinked();
            reloadLinks();
            setConfirm(null);
            toast(`Unlinked ${p.name}`, 'success');
        } catch (e) {
            toast(`Couldn’t unlink ${p.name}. ${e.message}`, 'error');
        } finally {
            setBusy(null);
        }
    };

    const experience = isExperience(user);
    const sponsors = providers.filter((p) => p.link_type === 'sponsorship');
    const dataLinks = providers.filter((p) => p.link_type === 'data');
    const firstName = (user?.name || '').trim().split(/\s+/)[0];
    const firstLoad = sess.loading && linkLoading && !sess.list.length && !providers.length;

    const recentCols = [
        {
            key: 'game',
            label: 'Game',
            w: 'minmax(0,1.4fr)',
            render: (r) => <span className="eg-table__text eg-table__text--strong" title={r.game}><GameCell game={r.game} origin={r.origin} /></span>,
        },
        { key: 'when', label: 'Played', w: 'minmax(0,1.2fr)' },
        { key: 'len', label: 'Length', w: 'minmax(0,.7fr)', align: 'right' },
        { key: 'status', label: 'Status', w: 'minmax(150px,1fr)', type: 'status' },
    ];
    const recentRows = recent.map((s) => ({
        id: s._id,
        game: s.game ? gameLabel(s.game) : '—',
        origin: startedByLabel(s, user, nameOf),
        when: fmtWhen(toMs(s.startTime)),
        len: fmtLength(s),
        status: own && own.sessionId === s._id ? 'live' : ['complete', 'pending', 'incomplete'].includes(s.consolidated) ? s.consolidated : null,
        onClick: () => navigate(`/sessions/${s._id}`),
    }));

    return (
        <div className="eg-page pld-page">
            <header className="pld-head">
                <div className="pld-head__text">
                    <span className="pld-greet">{greetingFor(new Date().getHours())}</span>
                    <h1 className="pld-h1">{firstName ? `Hi, ${firstName}` : 'Hi there'}</h1>
                    <span className="pld-activity">
                        {sess.loading ? <Skel w={320} h={20} /> : sess.error ? null : activityLine(sess.list)}
                    </span>
                </div>
                {own ? (
                    <Button variant="primary" size="xl" className="pld-cta" to={`/live/${own.sessionId}`} aria-label={`Open your live game${own.game ? `, ${gameLabel(own.game)}` : ''}`}>
                        <DotLabel color="var(--egdl-accent-text)" ink="var(--egdl-accent-text)" pulse large>Live now</DotLabel>
                        Open your game
                    </Button>
                ) : (
                    <Button variant="secondary" size="lg" className="pld-cta" icon="sessions" to="/sessions">Your sessions</Button>
                )}
            </header>

            {firstLoad ? (
                <div aria-busy="true" className="pld-grid">
                    <span className="eg-sr-only">Loading your dashboard…</span>
                    <Skel h={380} raised /><Skel h={380} raised />
                </div>
            ) : (
                <div className="pld-grid">
                    <section aria-labelledby="pld-rom-h" className="pld-card">
                        <div className="pld-sec-head">
                            <h2 id="pld-rom-h" className="eg-h2">Your range of motion</h2>
                        </div>
                        {sess.error ? <SectionError what="Your sessions" onRetry={loadSessions} />
                            : sess.loading ? <Skel h={300} raised />
                                : <RangeOfMotionChart data={jointsData} emptyText="No sessions yet" />}
                    </section>

                    <section aria-labelledby="pld-rv-h" className="pld-sec">
                        <div className="pld-sec-head pld-sec-head--stack">
                            <h2 id="pld-rv-h" className="eg-h2">Game reviews</h2>
                        </div>
                        {rev.error ? <SectionError what="Reviews" onRetry={loadReviews} />
                            : rev.loading ? <Skel h={300} raised />
                                : <GameReviewsChart reviews={rev.list} emptyText="No game reviews yet" />}
                    </section>

                    <section aria-labelledby="pld-sp-h" className="pld-sec">
                        <h2 id="pld-sp-h" className="eg-h2 pld-h2">Sponsorship</h2>
                        {linkError ? <SectionError what="Your linked accounts" onRetry={reloadLinks} />
                            : linkLoading && !providers.length ? <Skel h={120} raised />
                                : sponsors.length ? sponsors.map((p) => (
                                    <div key={p.link_id || p.user_id} className="pld-sponsor">
                                        <div className="pld-sponsor__top">
                                            <span className="pld-sponsor__name">{p.name}</span>
                                            {p.verified ? <StatusBadge status="active" /> : <StatusBadge status="pending" text="Awaiting provider" />}
                                        </div>
                                    </div>
                                )) : (
                                    <div className="pld-none">No sponsor</div>
                                )}
                    </section>

                    <section aria-labelledby="pld-la-h" className="pld-sec">
                        <h2 id="pld-la-h" className="eg-h2 pld-h2">Linked accounts</h2>
                        {linkError ? <SectionError what="Your linked accounts" onRetry={reloadLinks} />
                            : linkLoading && !providers.length ? <Skel h={160} raised />
                                : dataLinks.length ? dataLinks.map((p) => {
                                    const key = p.link_id || p.user_id;
                                    const asking = confirm === key;
                                    return (
                                        <div key={key} className="pld-link">
                                            <div className="pld-link__row">
                                                <span className="pld-link__icon" aria-hidden="true"><Icon name="profile" size={22} /></span>
                                                <span className="pld-link__text">
                                                    <span className="pld-link__name">{p.name}</span>
                                                    <span className="pld-link__meta">
                                                        {p.verified ? 'Your provider · sees your sessions' : 'Waiting for the provider to accept'}
                                                    </span>
                                                </span>
                                                {/* an experience account changes nothing on the portal (403 local_only) */}
                                                {experience ? null : (
                                                    <Button variant="secondary" size="lg" aria-label={`Unlink ${p.name}`} aria-expanded={asking} onClick={() => setConfirm(asking ? null : key)}>
                                                        Unlink
                                                    </Button>
                                                )}
                                            </div>
                                            {asking && !experience ? (
                                                <div className="pld-confirm" role="group" aria-label={`Confirm unlinking ${p.name}`}>
                                                    <span className="pld-confirm__text">Unlink {p.name}? They’ll stop seeing your sessions.</span>
                                                    <span className="pld-confirm__btns">
                                                        <Button variant="danger" size="lg" disabled={busy === p.link_id} onClick={() => unlink(p)}>
                                                            {busy === p.link_id ? 'Unlinking…' : 'Unlink'}
                                                        </Button>
                                                        <Button variant="ghost" muted size="lg" onClick={() => setConfirm(null)}>Cancel</Button>
                                                    </span>
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                }) : (
                                    <div className="pld-none">No linked providers</div>
                                )}
                    </section>

                    <section aria-labelledby="pld-rs-h" className="pld-sec pld-wide">
                        <div className="pld-sec-head">
                            <h2 id="pld-rs-h" className="eg-h2">Recent sessions</h2>
                            <span className="eg-spacer" />
                            <Button variant="ghost" to="/sessions" icon="sessions">All sessions</Button>
                        </div>
                        {sess.error ? <SectionError what="Your sessions" onRetry={loadSessions} />
                            : sess.loading ? <Skel h={220} raised />
                                : <DataTable caption="Recent sessions" columns={recentCols} rows={recentRows} getKey={(r) => r.id} emptyText="No sessions yet" />}
                    </section>

                    <section aria-labelledby="pld-st-h" className="pld-sec pld-wide pld-settings">
                        <h2 id="pld-st-h" className="eg-h2 pld-h2">Settings</h2>
                        <div className="pld-row">
                            <span className="pld-row__label">Name</span>
                            <span className="pld-row__value">{user?.name || '—'}</span>
                        </div>
                        {user?.email ? (
                            <div className="pld-row">
                                <span className="pld-row__label">Email</span>
                                <span className="pld-row__value">{user.email}</span>
                            </div>
                        ) : null}
                        {experience ? (
                            <div className="pld-row">
                                <span className="pld-row__label">Account</span>
                                <span className="pld-row__value">{experienceLabel(user.experienceEndsAt)}</span>
                            </div>
                        ) : null}
                        <div className="pld-row">
                            <span className="pld-row__label">Appearance</span>
                            <Segmented
                                label="Appearance"
                                size="lg"
                                value={theme}
                                onChange={setTheme}
                                options={[{ value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }]}
                            />
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
}

export default PlayerProfilePage;
